import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, SparklesIcon, UserIcon, PaintbrushIcon, MessageSquareIcon, DatabaseIcon, UserPlusIcon, BrainIcon, PlayIcon, RocketIcon, CheckIcon, XIcon, PlusIcon, CopyIcon, UploadIcon, SendIcon, HeadphonesIcon, LanguagesIcon } from 'lucide-react';
import { WidgetPreview, WidgetConfig } from './WidgetPreview';
const POSITIONS: WidgetConfig['position'][] = ['Bottom Right', 'Bottom Left', 'Floating Center', 'Embedded Page', 'Full Screen Assistant'];
const SIZES: WidgetConfig['size'][] = ['Compact', 'Standard', 'Expanded', 'Full Screen'];
const KNOWLEDGE = ['Inventory', 'Catalog', 'FAQs', 'Business Rules', 'Pricing Rules', 'Knowledge Base', 'Documents', 'Services', 'Policies', 'Terms & Conditions', 'Custom URLs'];
const LEAD_FIELDS = ['Name', 'Email', 'Mobile Number', 'WhatsApp', 'Location', 'Budget', 'Timeline'];
const TRIGGERS = ['Request callback', 'Request quotation', 'Request viewing', 'Request contact details', 'Request booking'];
const ESCALATION = ['WhatsApp', 'Email', 'Call', 'Live Chat', 'Support Ticket'];
const LANGUAGES = ['English', 'Arabic', 'Hindi', 'Urdu', 'Multi-language'];
const TONES = ['Professional', 'Friendly', 'Luxury', 'Corporate', 'Sales Focused', 'Custom'];
interface StepDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  hint: string;
}
const STEPS: StepDef[] = [{
  id: 'identity',
  label: 'Identity',
  icon: <UserIcon className="w-4 h-4" data-id="element-2276" />,
  hint: 'Name your AI employee'
}, {
  id: 'appearance',
  label: 'Appearance',
  icon: <PaintbrushIcon className="w-4 h-4" data-id="element-2277" />,
  hint: 'Match your brand'
}, {
  id: 'welcome',
  label: 'Welcome',
  icon: <MessageSquareIcon className="w-4 h-4" data-id="element-2278" />,
  hint: 'First impression'
}, {
  id: 'knowledge',
  label: 'Knowledge',
  icon: <DatabaseIcon className="w-4 h-4" data-id="element-2279" />,
  hint: 'What it can access'
}, {
  id: 'leads',
  label: 'Leads',
  icon: <UserPlusIcon className="w-4 h-4" data-id="element-2280" />,
  hint: 'Capture & qualify'
}, {
  id: 'behavior',
  label: 'Behavior',
  icon: <BrainIcon className="w-4 h-4" data-id="element-2281" />,
  hint: 'Teach the rules'
}, {
  id: 'test',
  label: 'Test',
  icon: <PlayIcon className="w-4 h-4" data-id="element-2282" />,
  hint: 'Try it live'
}, {
  id: 'deploy',
  label: 'Deploy',
  icon: <RocketIcon className="w-4 h-4" data-id="element-2283" />,
  hint: 'Go live'
}];
export function WidgetBuilder({
  onBack,
  onDeploy,
  initialStep
}: {
  onBack: () => void;
  onDeploy: () => void;
  initialStep?: string;
}) {
  const [stepIdx, setStepIdx] = useState(() => {
    const i = STEPS.findIndex(s => s.id === initialStep);
    return i >= 0 ? i : 0;
  });
  const step = STEPS[stepIdx].id;
  const showPreview = !['test', 'deploy'].includes(step);
  const [config, setConfig] = useState<WidgetConfig>({
    name: 'Talab Assistant',
    description: 'Helping customers find vehicles and answer questions.',
    greeting: "Welcome to Al Futtaim Motors 👋 I'm here to help you find the perfect vehicle.",
    prompts: ['Find me a Prado under AED 90,000', 'Do you offer delivery?', 'Can I book a viewing?', 'What financing options are available?'],
    primaryColor: '#4338CA',
    accentColor: '#A855F7',
    theme: 'light',
    position: 'Bottom Right',
    size: 'Standard',
    avatar: 'T',
    tone: 'Friendly'
  });
  const set = (patch: Partial<WidgetConfig>) => setConfig(c => ({
    ...c,
    ...patch
  }));
  const [knowledge, setKnowledge] = useState<string[]>(['Inventory', 'Catalog', 'FAQs', 'Pricing Rules']);
  const [leadFields, setLeadFields] = useState<string[]>(['Name', 'Mobile Number', 'Budget']);
  const [triggers, setTriggers] = useState<string[]>(['Request quotation', 'Request viewing']);
  const [escalation, setEscalation] = useState<string[]>(['WhatsApp']);
  const [languages, setLanguages] = useState<string[]>(['English', 'Arabic']);
  const [behavior, setBehavior] = useState('If customer asks about delivery inside Dubai, add AED 50.\nIf customer asks about airport pickup, add AED 75.\nAlways suggest GCC vehicles first.\nOffer financing when budget exceeds AED 80,000.');
  const toggle = (arr: string[], setArr: (v: string[]) => void, v: string) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const isLast = stepIdx === STEPS.length - 1;
  const next = () => setStepIdx(i => Math.min(STEPS.length - 1, i + 1));
  const prev = () => setStepIdx(i => Math.max(0, i - 1));
  return <div className="bg-white border border-slate-200 rounded-3xl shadow-soft overflow-hidden" data-id="element-2284">
      {/* Builder top bar */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3" data-id="element-2285">
        <div className="flex items-center gap-3 min-w-0" data-id="element-2286">
          <button onClick={onBack} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg flex-shrink-0" data-id="element-2287">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2288" />
          </button>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center flex-shrink-0" data-id="element-2289">
            <SparklesIcon className="w-5 h-5 text-white" data-id="element-2290" />
          </div>
          <div className="min-w-0" data-id="element-2291">
            <div className="font-display font-bold text-dark-navy truncate" data-id="element-2292">
              {config.name}
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1.5" data-id="element-2293">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-2294" /> Draft ·
              Website Widget
            </div>
          </div>
        </div>
        <button onClick={onDeploy} className="px-5 py-2 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 flex items-center gap-2 flex-shrink-0" data-id="element-2295">
          <RocketIcon className="w-4 h-4" data-id="element-2296" /> Deploy
        </button>
      </div>

      {/* Internal step nav bar */}
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60" data-id="element-2297">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-id="element-2298">
          {STEPS.map((s, i) => {
          const active = i === stepIdx;
          const done = i < stepIdx;
          return <Fragment key={s.id} data-id="element-2299">
                <button onClick={() => setStepIdx(i)} className={`flex items-center gap-2 px-3 py-2 rounded-xl whitespace-nowrap transition-colors flex-shrink-0 ${active ? 'bg-white shadow-sm border border-slate-200' : 'hover:bg-white/60'}`} data-id="element-2300">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-green-100 text-green-600' : active ? 'bg-primary-indigo text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-2301">
                    {done ? <CheckIcon className="w-3.5 h-3.5" data-id="element-2302" /> : i + 1}
                  </span>
                  <span className={`text-sm font-medium ${active ? 'text-dark-navy' : done ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-2303">
                    {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && <span className="w-4 h-px bg-slate-200 flex-shrink-0" data-id="element-2304" />}
              </Fragment>;
        })}
        </div>
      </div>

      {/* Body: focused step + live preview */}
      <div className="flex flex-col lg:flex-row" data-id="element-2305">
        <div className={`flex-1 p-6 lg:p-8 ${showPreview ? '' : 'max-w-3xl mx-auto w-full'}`} data-id="element-2306">
          <div className="mb-6" data-id="element-2307">
            <div className="flex items-center gap-2 text-primary-indigo mb-1" data-id="element-2308">
              {STEPS[stepIdx].icon}
              <span className="text-xs font-bold uppercase tracking-wider" data-id="element-2309">
                Step {stepIdx + 1} of {STEPS.length}
              </span>
            </div>
            <h3 className="text-2xl font-display font-bold text-dark-navy" data-id="element-2310">
              {STEPS[stepIdx].label}
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-2311">{STEPS[stepIdx].hint}</p>
          </div>

          <AnimatePresence mode="wait" data-id="element-2312">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} className="space-y-5" data-id="element-2313">
              {step === 'identity' && <>
                  <Field label="Agent name" value={config.name} onChange={v => set({
                name: v
              })} placeholder="Talab Assistant" data-id="element-2314" />
                  <Field label="Description" value={config.description} onChange={v => set({
                description: v
              })} textarea placeholder="What does this agent help with?" data-id="element-2315" />
                  <div data-id="element-2316">
                    <Lbl data-id="element-2317">Avatar</Lbl>
                    <div className="flex items-center gap-3" data-id="element-2318">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold" style={{
                    backgroundColor: config.primaryColor
                  }} data-id="element-2319">
                        {config.avatar || 'T'}
                      </div>
                      <Upload label="" data-id="element-2320" />
                    </div>
                  </div>
                </>}

              {step === 'appearance' && <>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2321">
                    <Color label="Primary color" value={config.primaryColor} onChange={v => set({
                  primaryColor: v
                })} data-id="element-2322" />
                    <Color label="Accent color" value={config.accentColor} onChange={v => set({
                  accentColor: v
                })} data-id="element-2323" />
                  </div>
                  <Segment label="Theme" options={['light', 'dark']} value={config.theme} onChange={v => set({
                theme: v as WidgetConfig['theme']
              })} data-id="element-2324" />
                  <Segment label="Position" options={POSITIONS} value={config.position} onChange={v => set({
                position: v as WidgetConfig['position']
              })} data-id="element-2325" />
                  <Segment label="Size" options={SIZES} value={config.size} onChange={v => set({
                size: v as WidgetConfig['size']
              })} data-id="element-2326" />
                </>}

              {step === 'welcome' && <>
                  <Field label="Greeting message" value={config.greeting} onChange={v => set({
                greeting: v
              })} textarea data-id="element-2327" />
                  <PromptEditor prompts={config.prompts} onChange={prompts => set({
                prompts
              })} data-id="element-2328" />
                </>}

              {step === 'knowledge' && <div data-id="element-2329">
                  <Lbl data-id="element-2330">Let your agent answer using</Lbl>
                  <ChipGroup options={KNOWLEDGE} selected={knowledge} onToggle={v => toggle(knowledge, setKnowledge, v)} data-id="element-2331" />
                </div>}

              {step === 'leads' && <>
                  <div data-id="element-2332">
                    <Lbl data-id="element-2333">Collect from customers</Lbl>
                    <ChipGroup options={LEAD_FIELDS} selected={leadFields} onToggle={v => toggle(leadFields, setLeadFields, v)} data-id="element-2334" />
                  </div>
                  <div data-id="element-2335">
                    <Lbl className="mt-2" data-id="element-2336">
                      Create a lead when customer asks to
                    </Lbl>
                    <ChipGroup options={TRIGGERS} selected={triggers} onToggle={v => toggle(triggers, setTriggers, v)} data-id="element-2337" />
                  </div>
                </>}

              {step === 'behavior' && <>
                  <div className="rounded-2xl p-4 border border-ai-purple/30 bg-purple-50/40" data-id="element-2338">
                    <div className="flex items-center gap-2 mb-2" data-id="element-2339">
                      <BrainIcon className="w-4 h-4 text-ai-purple" data-id="element-2340" />
                      <span className="font-bold text-dark-navy text-sm" data-id="element-2341">
                        Teach your agent — in plain language
                      </span>
                    </div>
                    <textarea value={behavior} onChange={e => setBehavior(e.target.value)} rows={5} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20 resize-none" data-id="element-2342" />
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-2343">
                      {behavior.split('\n').filter(Boolean).map((_, i) => <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white text-ai-purple text-xs font-medium rounded-full border border-purple-100" data-id="element-2344">
                            <CheckIcon className="w-3 h-3" data-id="element-2345" /> Rule {i + 1}{' '}
                            structured
                          </span>)}
                    </div>
                  </div>
                  <div data-id="element-2346">
                    <Lbl className="flex items-center gap-1.5" data-id="element-2347">
                      <HeadphonesIcon className="w-3.5 h-3.5" data-id="element-2348" /> Escalate to a
                      human via
                    </Lbl>
                    <ChipGroup options={ESCALATION} selected={escalation} onToggle={v => toggle(escalation, setEscalation, v)} data-id="element-2349" />
                  </div>
                  <div data-id="element-2350">
                    <Lbl className="flex items-center gap-1.5" data-id="element-2351">
                      <LanguagesIcon className="w-3.5 h-3.5" data-id="element-2352" /> Languages
                    </Lbl>
                    <ChipGroup options={LANGUAGES} selected={languages} onToggle={v => toggle(languages, setLanguages, v)} data-id="element-2353" />
                  </div>
                  <Segment label="Tone" options={TONES} value={config.tone} onChange={v => set({
                tone: v
              })} data-id="element-2354" />
                </>}

              {step === 'test' && <CustomerPreview config={config} data-id="element-2355" />}
              {step === 'deploy' && <DeployView config={config} data-id="element-2356" />}
            </motion.div>
          </AnimatePresence>

          {/* Footer nav */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100" data-id="element-2357">
            <button onClick={prev} disabled={stepIdx === 0} className={`px-5 py-2.5 font-medium rounded-xl transition-colors flex items-center gap-2 ${stepIdx === 0 ? 'opacity-0 pointer-events-none' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-2358">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-2359" /> Back
            </button>
            {isLast ? <button onClick={onDeploy} className="px-6 py-2.5 bg-primary-indigo text-white font-bold rounded-xl hover:bg-indigo-700 flex items-center gap-2 shadow-md" data-id="element-2360">
                <RocketIcon className="w-4 h-4" data-id="element-2361" /> Deploy Agent
              </button> : <button onClick={next} className="px-6 py-2.5 bg-dark-navy text-white font-medium rounded-xl hover:bg-slate-800 flex items-center gap-2 shadow-md" data-id="element-2362">
                Continue <ArrowRightIcon className="w-4 h-4" data-id="element-2363" />
              </button>}
          </div>
        </div>

        {/* Live preview */}
        {showPreview && <div className="hidden lg:flex flex-col w-[400px] flex-shrink-0 border-l border-slate-100 bg-slate-100 relative" data-id="element-2364">
            <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-white/80 backdrop-blur rounded-full text-xs font-medium text-slate-500 flex items-center gap-1.5 z-10 shadow-sm" data-id="element-2365">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-2366" />{' '}
              Live Preview
            </div>
            <div className="flex-1 p-6 pt-16 overflow-hidden bg-[radial-gradient(circle_at_center,#e2e8f0_1px,transparent_1px)] [background-size:18px_18px]" data-id="element-2367">
              <WidgetPreview config={config} data-id="element-2368" />
            </div>
          </div>}
      </div>
    </div>;
}
/* ---------- Customer Preview ---------- */
function CustomerPreview({
  config
}: {
  config: WidgetConfig;
}) {
  const [msgs, setMsgs] = useState<{
    role: 'c' | 'a';
    t: string;
  }[]>([{
    role: 'a',
    t: config.greeting
  }]);
  const [input, setInput] = useState('');
  const reply = (q: string) => {
    if (!q.trim()) return;
    const l = q.toLowerCase();
    let a = 'I can help with that. Could you share a few more details?';
    if (l.includes('deliver')) a = 'Yes. Delivery inside Dubai is AED 50.';else if (l.includes('financ')) a = 'Yes. Financing options are available for vehicles above AED 80,000.';else if (l.includes('airport')) a = 'Airport pickup is available for AED 75.';else if (l.includes('prado')) a = 'I found 3 GCC Prado vehicles under AED 90,000. Want to see them?';
    setMsgs(m => [...m, {
      role: 'c',
      t: q
    }, {
      role: 'a',
      t: a
    }]);
    setInput('');
  };
  return <div className="max-w-lg mx-auto w-full bg-white border border-slate-200 rounded-2xl shadow-soft flex flex-col overflow-hidden h-[420px]" data-id="element-2369">
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-2370">
        {msgs.map((m, i) => <div key={i} className={`flex ${m.role === 'c' ? 'justify-end' : 'gap-2'}`} data-id="element-2371">
            {m.role === 'a' && <div className="w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{
          backgroundColor: config.primaryColor
        }} data-id="element-2372">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-2373" />
              </div>}
            <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${m.role === 'c' ? 'text-white rounded-tr-sm' : 'bg-slate-100 text-slate-800 rounded-tl-sm'}`} style={m.role === 'c' ? {
          backgroundColor: config.primaryColor
        } : {}} data-id="element-2374">
              {m.t}
            </div>
          </div>)}
      </div>
      <div className="p-3 border-t border-slate-100 flex items-center gap-2" data-id="element-2375">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && reply(input)} placeholder="Test the agent…" className="flex-1 px-3 py-2 bg-slate-100 rounded-full text-sm outline-none" data-id="element-2376" />
        <button onClick={() => reply(input)} className="w-9 h-9 rounded-full text-white flex items-center justify-center flex-shrink-0" style={{
        backgroundColor: config.primaryColor
      }} data-id="element-2377">
          <SendIcon className="w-4 h-4" data-id="element-2378" />
        </button>
      </div>
    </div>;
}
/* ---------- Deploy ---------- */
function DeployView({
  config
}: {
  config: WidgetConfig;
}) {
  const [copied, setCopied] = useState(false);
  const snippet = `<script src="https://cdn.talab.ai/widget.js"\n  data-agent="${config.name.toLowerCase().replace(/\s+/g, '-')}"\n  data-key="pk_live_8f2a..."\n  async>\n</script>`;
  return <div className="space-y-6" data-id="element-2379">
      <div className="bg-slate-900 rounded-2xl p-5 relative" data-id="element-2380">
        <button onClick={() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }} className="absolute top-4 right-4 px-3 py-1.5 bg-white/10 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-white/20" data-id="element-2381">
          {copied ? <>
              <CheckIcon className="w-3.5 h-3.5" data-id="element-2382" /> Copied
            </> : <>
              <CopyIcon className="w-3.5 h-3.5" data-id="element-2383" /> Copy
            </>}
        </button>
        <pre className="text-sm text-green-300 font-mono whitespace-pre-wrap leading-relaxed" data-id="element-2384">
          {snippet}
        </pre>
      </div>
      <div data-id="element-2385">
        <Lbl data-id="element-2386">One-click installs</Lbl>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-2387">
          {['WordPress', 'Shopify', 'Wix', 'Webflow', 'Custom Website'].map(p => <button key={p} className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-dark-navy hover:border-primary-indigo/40 shadow-soft text-left" data-id="element-2388">
                {p}
              </button>)}
        </div>
      </div>
      <div data-id="element-2389">
        <Lbl data-id="element-2390">Allowed domains</Lbl>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2 shadow-soft" data-id="element-2391">
          {['example.com', 'www.example.com', 'shop.example.com'].map(d => <div key={d} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg text-sm" data-id="element-2392">
              <span className="text-slate-700 font-mono" data-id="element-2393">{d}</span>
              <XIcon className="w-4 h-4 text-slate-300 hover:text-red-400 cursor-pointer" data-id="element-2394" />
            </div>)}
          <button className="flex items-center gap-1.5 text-sm text-primary-indigo font-medium px-3 py-1.5" data-id="element-2395">
            <PlusIcon className="w-4 h-4" data-id="element-2396" /> Add domain
          </button>
        </div>
      </div>
    </div>;
}
/* ---------- Controls ---------- */
function Lbl({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <label className={`block text-sm font-medium text-slate-700 mb-2 ${className}`} data-id="element-2397">
      {children}
    </label>;
}
function Field({
  label,
  value,
  onChange,
  textarea,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
  placeholder?: string;
}) {
  return <div data-id="element-2398">
      <Lbl data-id="element-2399">{label}</Lbl>
      {textarea ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} placeholder={placeholder} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20 resize-none" data-id="element-2400" /> : <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2401" />}
    </div>;
}
function Color({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return <div data-id="element-2402">
      <Lbl data-id="element-2403">{label}</Lbl>
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5" data-id="element-2404">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" data-id="element-2405" />
        <span className="text-sm text-slate-600 font-mono" data-id="element-2406">{value}</span>
      </div>
    </div>;
}
function Upload({
  label
}: {
  label: string;
}) {
  return <button className="flex-1 border-2 border-dashed border-slate-200 rounded-xl py-4 flex flex-col items-center gap-1 text-slate-400 hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors" data-id="element-2407">
      <UploadIcon className="w-5 h-5" data-id="element-2408" />
      <span className="text-xs" data-id="element-2409">Upload {label}</span>
    </button>;
}
function Segment({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return <div data-id="element-2410">
      <Lbl data-id="element-2411">{label}</Lbl>
      <div className="flex flex-wrap gap-2" data-id="element-2412">
        {options.map(o => <button key={o} onClick={() => onChange(o)} className={`px-4 py-2 text-sm font-medium rounded-xl border transition-colors capitalize ${value === o ? 'bg-primary-indigo text-white border-primary-indigo' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-2413">
            {o}
          </button>)}
      </div>
    </div>;
}
function ChipGroup({
  options,
  selected,
  onToggle
}: {
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return <div className="flex flex-wrap gap-2" data-id="element-2414">
      {options.map(o => {
      const on = selected.includes(o);
      return <button key={o} onClick={() => onToggle(o)} className={`px-3.5 py-2 text-sm font-medium rounded-xl border transition-colors flex items-center gap-1.5 ${on ? 'bg-indigo-50 text-primary-indigo border-primary-indigo/30' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-2415">
            {on && <CheckIcon className="w-3.5 h-3.5" data-id="element-2416" />} {o}
          </button>;
    })}
    </div>;
}
function PromptEditor({
  prompts,
  onChange
}: {
  prompts: string[];
  onChange: (p: string[]) => void;
}) {
  return <div data-id="element-2417">
      <Lbl data-id="element-2418">Suggested prompts</Lbl>
      <div className="space-y-2" data-id="element-2419">
        {prompts.map((p, i) => <div key={i} className="flex items-center gap-2" data-id="element-2420">
            <input value={p} onChange={e => onChange(prompts.map((x, j) => j === i ? e.target.value : x))} className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2421" />
            <button onClick={() => onChange(prompts.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-400" data-id="element-2422">
              <XIcon className="w-4 h-4" data-id="element-2423" />
            </button>
          </div>)}
        <button onClick={() => onChange([...prompts, ''])} className="flex items-center gap-1.5 text-sm text-primary-indigo font-medium" data-id="element-2424">
          <PlusIcon className="w-4 h-4" data-id="element-2425" /> Add prompt
        </button>
      </div>
    </div>;
}